import { createSelector } from "@reduxjs/toolkit";
import { StratifyDocument } from "@stratify/shared";
import { selectDocuments, selectDocumentsFilters } from "./documentsSelectors";

const sortDocuments = (
  documents: StratifyDocument[],
  sortBy: string,
  sortOrder: "asc" | "desc"
): StratifyDocument[] => {
  const order = sortOrder === "asc" ? 1 : -1;

  return [...documents].sort((a, b) => {
    switch (sortBy) {
      case "filename":
        return order * a.filename.localeCompare(b.filename);
      case "type":
        return order * a.type.localeCompare(b.type);
      case "size":
        return order * (a.size - b.size);
      case "metadata.category":
        return (
          order *
          (a.metadata.category?.name || "").localeCompare(
            b.metadata.category?.name || ""
          )
        );
      case "metadata.processing.status":
        return (
          order *
          a.metadata.processing.status.localeCompare(
            b.metadata.processing.status
          )
        );
      case "metadata.address":
        return (
          order *
          (a.metadata.address || "").localeCompare(b.metadata.address || "")
        );
      case "createdAt":
        return (
          order *
          (a.createdAt.toDate().getTime() - b.createdAt.toDate().getTime())
        );
      default:
        return 0;
    }
  });
};

export const selectFilteredDocuments = createSelector(
  [selectDocuments, selectDocumentsFilters],
  (documents, filters): StratifyDocument[] => {
    let filtered = documents;

    // Apply search filter
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((doc) =>
        doc.filename.toLowerCase().includes(searchLower)
      );
    }

    // Apply type filter
    if (filters.type?.length) {
      filtered = filtered.filter((doc) => filters.type?.includes(doc.type));
    }

    // Apply category filter
    if (filters.category?.length) {
      filtered = filtered.filter(
        (doc) =>
          doc.metadata.category &&
          filters.category?.includes(doc.metadata.category.id)
      );
    }

    // Apply processing status filter
    if (filters.processingStatus?.length) {
      filtered = filtered.filter((doc) =>
        filters.processingStatus?.includes(doc.metadata.processing.status)
      );
    }

    // Apply address filter
    if (filters.address) {
      const addressLower = filters.address.toLowerCase();
      filtered = filtered.filter((doc) =>
        doc.metadata.address?.toLowerCase().includes(addressLower)
      );
    }

    // Apply sorting
    return sortDocuments(filtered, filters.sortBy, filters.sortOrder);
  }
);
